import { type PutObjectCommandInput, S3Client } from '@aws-sdk/client-s3';
import { Upload } from '@aws-sdk/lib-storage';
import { XhrHttpHandler } from '@aws-sdk/xhr-http-handler';
import { DateTime } from 'luxon';

import { type AppConfig } from '../../models/app.config';
import { type S3Credentials } from '../../models/s3/s3-credentials';

import { type S3Folder } from '../../models/s3/s3-folders';
import { type S3UploadedFile } from '../../models/s3/s3-uploaded-file';
import { type UploadToS3Params } from '../../models/s3/upload-to-s3-params';
import { selectAwsCredentials, updateAwsCredentials } from '../../store/files-upload-store';
import { S3UploadDataMapper } from '../mappers/s3-upload-data.mapper';

export namespace S3Service {

  /**
   * Get cached temporary AWS credential or fetch new ones if the previous have expired.
   *
   * Credentials are considered expired once their `expiration` timestamp
   * is in the past relative to `DateTime.now()`.
   */
  async function getTempCredentials(): Promise<S3Credentials> {
    const credentials = selectAwsCredentials();

    if (credentials) {
      const isExpired = DateTime.now()
        .diff(DateTime.fromJSDate(credentials.expiration))
        .as('seconds') >= 0;
      if (!isExpired) {
        return credentials;
      }
    }

    try {
      // TODO: Request temporary credentials from the backend and remove mock data
      // const tempCredentials = await awsApi.getTemporaryCredentials();

      /* eslint-disable @typescript-eslint/naming-convention -- backend credential DTO uses snake_case */
      const tempCredentials = await Promise.resolve({
        data: {
          access_key_id: 'access_key_id',
          expiration: '2023-01-01T00:00:00.000Z',
          secret_access_key: 'secret_access_key',
          session_token: 'session_token',
        },
      });
      /* eslint-enable @typescript-eslint/naming-convention */

      const awsCredentials = S3UploadDataMapper.fromS3CredentialsDto(tempCredentials.data);
      updateAwsCredentials(awsCredentials);
      return awsCredentials;
    } catch (error: unknown) {
      // TODO: Improve error type narrowing
      if (error instanceof Error) {
        throw error;
      }
      throw error;
    }
  }

  /**
   * Get S3 client instance.
   * @param config App configuration containing S3 settings.
   */
  async function getS3Client(config: AppConfig): Promise<S3Client> {
    const tempCredentials = await getTempCredentials();

    return new S3Client({
      // Replace default handler to allow progress tracking for files of less than 5MB
      // Source: https://www.npmjs.com/package/@aws-sdk/xhr-http-handler
      requestHandler: new XhrHttpHandler({}),
      endpoint: config.s3Endpoint,
      region: config.s3Region,
      credentials: tempCredentials,
      requestChecksumCalculation: 'WHEN_REQUIRED',
    });
  }

  /**
   * Get S3 upload params for a file.
   * @param file File to upload.
   * @param folder S3 bucket Folder name.
   * @param config App configuration containing S3 settings.
   */
  function getS3Params(file: File, folder: S3Folder, config: AppConfig): PutObjectCommandInput {
    const key = `${folder}/${self.crypto.randomUUID()}/${file.name}`;

    /* eslint-disable @typescript-eslint/naming-convention -- PutObjectCommandInput uses AWS SDK property names */
    const params: PutObjectCommandInput = {
      Bucket: config.s3Bucket,
      Key: key,
      Body: file,
    };
    /* eslint-enable @typescript-eslint/naming-convention */

    return params;
  }

  /**
   * Upload file to S3.
   * @param file File to upload to S3 bucket.
   * @param folder S3 bucket folder name.
   * @param signal Abort signal.
   * @param config App configuration containing S3 settings.
   */
  export async function uploadFileToS3({
    file,
    folder,
    signal,
    onProgress,
    config,
  }: UploadToS3Params): Promise<S3UploadedFile> {
    const s3Client = await getS3Client(config);
    const params = getS3Params(file.file, folder, config);

    const upload = new Upload({
      client: s3Client,
      params,
    });

    upload.on('httpUploadProgress', ({ loaded, total }) => {
      const progressPercentage = loaded && total ? Math.round((loaded / total) * 100) : null;

      // Skip updating state if the upload has been aborted
      // Multipart uploads may still fire progress events after cancellation due to chunked transfers
      if (!signal?.aborted && progressPercentage !== null) {
        onProgress?.(file, progressPercentage);
      }
    });

    const cancelUpload: VoidFunction = () => {
      upload.abort();
    };

    const addAbortListener = () => {
      signal?.addEventListener('abort', cancelUpload);
      return () => signal?.removeEventListener('abort', cancelUpload);
    };

    const removeListener = addAbortListener();

    try {
      const result = await upload.done();

      // Network error fallback: AWS SDK returns code "0" on connection failure without any errors
      // Handles the case when all parts of a multipart upload have been sent, but the upload itself has not finished
      if (result.$metadata.httpStatusCode === 0) {
        throw new Error('Connection failed.');
      }

      if (!result.Location) {
        throw new Error('"Location" property is missing from the return object.');
      }

      return {
        id: file.id,
        url: result.Location,
      };
    } finally {
      removeListener();
    }
  }
}
